import { metricsAxios } from 'api/apiBase/axios';
import { IBaseResponse } from 'api/apiBase/IBaseResponse';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';

import {
  GetOrdersRequest,
  GetOrdersResponse,
  PlaceLimitOrderModel,
  PlaceLimitOrderRequest,
  PlaceLimitOrderResponse,
} from './api.types';

// TODO: remove it and use original id instead of orderId when cex-trading-widget will be ready.
const mapPlaceLimitOrderResponse = (data: PlaceLimitOrderResponse): PlaceLimitOrderModel => ({
  orderId: data.id,
});

/**
 * Retrieves filtered exchange orders based on the supplied criteria.
 *
 * @param req - Filter parameters used to query orders.
 * @returns A promise resolving to the mapped orders response or a mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/order/get_api_v1_orders
 */
export async function getOrders(req: GetOrdersRequest) {
  try {
    const response = await metricsAxios.post<IBaseResponse<GetOrdersResponse>>(
      '/api/v2/orders/filter',
      req,
    );

    return mapResponse({ data: response.data });
  } catch (error) {
    return mapError<GetOrdersResponse>({ error });
  }
}

const ORDERS_PAGE_SIZE = 100;

export async function getAllOrders(req: Omit<GetOrdersRequest, 'limit' | 'offset'>) {
  let offset = 0;
  let totalCount = 0;
  const orders: GetOrdersResponse['orders'] = [];

  do {
    const response = await getOrders({
      ...req,
      limit: ORDERS_PAGE_SIZE,
      offset,
    });

    if (!response.isSuccess) {
      return response;
    }

    const pageOrders = response.data?.orders ?? [];
    totalCount = response.data?.total_count ?? 0;
    orders.push(...pageOrders);

    if (pageOrders.length === 0) {
      break;
    }

    offset += pageOrders.length;
  } while (orders.length < totalCount);

  return mapResponse<GetOrdersResponse>({
    data: {
      orders,
      total_count: totalCount,
    },
  });
}

/**
 * Submits a new limit order.
 *
 * @param req - Payload containing the limit order configuration.
 * @returns A promise resolving to the normalized limit order response or a mapped error.
 * @see https://api-metrics-dev.111pg.info/swagger/index.html#/order/post_api_v1_orders
 */
export async function placeLimitOrder(req: PlaceLimitOrderRequest) {
  try {
    const response = await metricsAxios.post<PlaceLimitOrderResponse>('/api/v1/orders', req);

    return mapResponse<PlaceLimitOrderModel>({
      data: mapPlaceLimitOrderResponse(response.data),
    });
  } catch (error) {
    return mapError<PlaceLimitOrderModel>({ error });
  }
}
